import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';

import { BaseAlertComponent } from '../../alerts/base-alert/base-alert.component';
import { AdminUserService } from '../../../services/admin-user.service';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-admin-setup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, BaseAlertComponent],
  templateUrl: './admin-setup.component.html',
  styleUrls: ['./admin-setup.component.scss'],
})
export class AdminSetupComponent {
  setupForm: UntypedFormGroup;
  saving = false;
  submitted = false;
  errorMessage = '';
  successMessage = '';

  constructor(
    private formBuilder: UntypedFormBuilder,
    private adminUserService: AdminUserService,
    private toastService: ToastService,
    private router: Router,
  ) {
    this.setupForm = this.formBuilder.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      affiliation: [''],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]],
    });
  }

  get passwordsDoNotMatch(): boolean {
    return (
      this.setupForm.controls.confirmPassword.value?.length > 0 &&
      this.setupForm.controls.password.value !== this.setupForm.controls.confirmPassword.value
    );
  }

  createSuperAdmin(): void {
    this.submitted = true;
    this.errorMessage = '';
    this.successMessage = '';

    if (this.setupForm.invalid || this.passwordsDoNotMatch || this.saving) {
      return;
    }

    this.saving = true;
    const value = this.setupForm.value;

    this.adminUserService
      .createSuperAdmin({
        firstName: value.firstName.trim(),
        lastName: value.lastName.trim(),
        email: value.email.trim(),
        password: value.password,
        affiliation: value.affiliation?.trim() || undefined,
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.successMessage = 'Super Administrator account created.';
          this.toastService.success('Setup complete', this.successMessage);
          window.setTimeout(() => this.router.navigate(['/login']), 2000);
        },
        error: (error) => {
          console.error('Could not create Super Administrator', error);
          this.saving = false;
          this.errorMessage = this.extractErrorMessage(
            error,
            'Could not create the Super Administrator account.',
          );
          this.toastService.error('Setup failed', this.errorMessage);
        },
      });
  }

  clearError(): void {
    this.errorMessage = '';
  }

  private extractErrorMessage(error: any, fallback: string): string {
    if (error?.error?.detail) {
      return error.error.detail;
    }

    if (error?.error?.message) {
      return error.error.message;
    }

    if (typeof error?.error === 'string') {
      return error.error;
    }

    if (error?.message) {
      return error.message;
    }

    return fallback;
  }
}
